import React from 'react';
import { CompanyName } from '../../models';
import { Clock, ShieldAlert, Ban, LogOut, Phone, AlertTriangle } from 'lucide-react';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';

export type AccessDeniedReason = 'expired' | 'suspended' | 'not_enrolled';

interface AccessCheckProps {
    reason: AccessDeniedReason;
    dueAt?: any; // Can be number or Timestamp
    onLogout: () => void;
    branchPhoneNumber?: string;
}

const AccessCheck: React.FC<AccessCheckProps> = ({ reason, dueAt, onLogout, branchPhoneNumber }) => {
    const dueMillis = dueAt?.toMillis?.() || dueAt || 0;
    const expiryDate = new Date(dueMillis).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const handleLogout = async () => {
        await signOut(auth);
        onLogout();
    };

    const renderContent = () => {
        switch (reason) {
            case 'suspended':
                return {
                    icon: Ban,
                    color: 'text-slate-500',
                    bgColor: 'bg-slate-100',
                    label: 'Account Suspended',
                    title: <>계정이 <span className="text-slate-500">일시 정지되었습니다</span></>,
                    desc: <>운영 정책 위반 또는 관리자에 의해 <br /> 계정 사용이 제한되었습니다.</>,
                    subInfo: (
                        <div className="bg-slate-50 w-full rounded-3xl p-6 md:p-8 text-center">
                            <p className="text-xs text-slate-400 font-medium leading-relaxed">
                                자세한 사유는 관리자에게 문의해 주세요. <br />
                                소명이 필요하신 경우 고객센터를 이용해 주세요.
                            </p>
                        </div>
                    )
                };
            case 'not_enrolled':
                return {
                    icon: AlertTriangle,
                    color: 'text-amber-500',
                    bgColor: 'bg-amber-50',
                    label: 'Access Denied',
                    title: <>현재 <span className="text-amber-500">재원생 상태가 아닙니다</span></>,
                    desc: <>학습 서비스를 이용하시려면 <br /> 관리자의 승인이 필요하거나 등록이 필요합니다.</>,
                    subInfo: (
                        <div className="bg-slate-50 w-full rounded-3xl p-6 md:p-8 text-center">
                            <p className="text-xs text-slate-400 font-medium leading-relaxed">
                                입학 문의 또는 재원 상태 변경 요청은 <br />
                                학원 관리자에게 문의 바랍니다.
                            </p>
                        </div>
                    )
                };
            case 'expired':
            default:
                return {
                    icon: ShieldAlert,
                    color: 'text-rose-500',
                    bgColor: 'bg-rose-50',
                    label: 'Access Expired',
                    title: <>멤버십 기간이 <br /> <span className="text-rose-500">만료되었습니다</span></>,
                    desc: <>학습 관리 서비스 이용 기간이 종료되었습니다. <br /> 계속해서 서비스를 이용하시려면 연장이 필요합니다.</>,
                    subInfo: (
                        <div className="bg-slate-50 w-full rounded-3xl p-6 md:p-8 space-y-4">
                            <div className="flex items-center justify-between text-sm font-bold">
                                <span className="text-slate-400 flex items-center gap-2"><Clock size={16} /> 만료 일자</span>
                                <span className="text-slate-800">{expiryDate}</span>
                            </div>
                            <div className="h-[1px] bg-slate-200/50"></div>
                            <p className="text-xs text-slate-400 font-medium leading-relaxed text-center">
                                연장 신청은 학원 관리자에게 문의해 주세요. <br />
                                결제 정보 확인 후 즉시 재활성화됩니다.
                            </p>
                        </div>
                    )
                };
        }
    };

    const content = renderContent();
    const Icon = content.icon;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 md:p-8 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-full h-full pointer-events-none">
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] blur-[150px] rounded-full ${reason === 'expired' ? 'bg-rose-50/50' : reason === 'not_enrolled' ? 'bg-amber-50/50' : 'bg-slate-100/50'}`}></div>
            </div>

            <div className={`w-full max-w-xl bg-white rounded-[2.5rem] md:rounded-[4rem] p-8 md:p-16 shadow-2xl relative border flex flex-col items-center text-center space-y-8 md:space-y-10 animate-in fade-in zoom-in duration-700 ${reason === 'expired' ? 'border-rose-100' : reason === 'not_enrolled' ? 'border-amber-100' : 'border-slate-100'}`}>
                <div className={`w-24 h-24 rounded-[2.5rem] flex items-center justify-center shadow-inner border animate-bounce transition-all duration-1000 ${content.bgColor} ${content.color} border-white`}>
                    <Icon size={48} strokeWidth={1.5} />
                </div>

                <div className="space-y-6">
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-black text-[10px] uppercase tracking-wider ${content.bgColor} ${content.color}`}>
                        {content.label}
                    </div>
                    <h2 className="text-2xl md:text-4xl font-black text-slate-800 tracking-tight leading-tight">
                        {content.title}
                    </h2>
                    <p className="text-slate-400 font-medium leading-relaxed max-w-sm mx-auto">
                        {content.desc}
                    </p>
                </div>

                {content.subInfo}

                <div className="w-full">
                    <button
                        onClick={() => window.location.href = `tel:${branchPhoneNumber || ''}`}
                        className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-100 py-4 rounded-2xl font-black text-slate-600 hover:bg-slate-50 transition-all active:scale-95"
                    >
                        <Phone size={18} /> 전화 문의 ({branchPhoneNumber || '번호 없음'})
                    </button>
                    <button
                        onClick={handleLogout}
                        className="mt-6 flex items-center justify-center gap-2 w-full text-slate-300 hover:text-rose-400 transition-colors font-bold text-[10px] uppercase tracking-widest"
                    >
                        로그아웃
                    </button>
                </div>
            </div>


            <p className="mt-12 text-[10px] font-black text-slate-300 uppercase tracking-widest">
                {CompanyName} ACCESS VERIFICATION SYSTEM
            </p>
        </div>
    );
};

export default AccessCheck;
